import { PageHero } from '@components/common';
import { Users, ArrowRight, Shield, Target } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

const idealFor = [
  'Boards preparing for their first incident response exercise',
  'Leadership teams wanting to validate response plans',
  'Companies with new or updated incident response procedures',
  'Organisations in regulated industries with incident reporting requirements',
  'Teams who want to practice under pressure without real consequences',
  'Boards seeking to understand their role during a crisis',
];

const deliverables = [
  {
    title: 'Customised Exercise Scenario',
    description: 'Realistic incident scenario tailored to your industry and threat profile',
  },
  {
    title: 'Facilitated Exercise Session',
    description: 'Expert-led tabletop exercise with structured decision points and injects',
  },
  {
    title: 'Decision Timeline Analysis',
    description: 'Review of key decisions, timing, and communication flow during the exercise',
  },
  {
    title: 'Gap Analysis Report',
    description: 'Identification of gaps in plans, processes, and team readiness',
  },
  {
    title: 'Improvement Recommendations',
    description: 'Actionable recommendations to strengthen incident response capability',
  },
];

const scenarios = [
  {
    title: 'Ransomware Attack',
    description: 'Critical systems encrypted, operations halted, ransom demand received',
  },
  {
    title: 'Data Breach',
    description: 'Customer data exfiltrated, regulatory notification required, media interest',
  },
  {
    title: 'Supply Chain Compromise',
    description: 'Trusted vendor compromised, malware spreading through your network',
  },
  {
    title: 'Insider Threat',
    description: 'Employee suspected of data theft, legal and HR considerations',
  },
];

const process = [
  {
    step: '01',
    title: 'Preparation',
    description: 'Scenario selection, participant briefing, and logistics planning',
    duration: '1 week before',
  },
  {
    step: '02',
    title: 'The Exercise',
    description: 'Facilitated 2-3 hour tabletop session with realistic injects and decisions',
    duration: 'Half day',
  },
  {
    step: '03',
    title: 'Hot Wash',
    description: 'Immediate feedback session immediately following the exercise',
    duration: '30 minutes',
  },
  {
    step: '04',
    title: 'Reporting',
    description: 'Comprehensive report with findings and improvement recommendations',
    duration: '3-5 days',
  },
];

export default function BoardTabletopPage() {
  const packageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Board Tabletop Exercises',
    description:
      'Interactive incident response exercises for senior leadership. Practice decision-making under pressure and validate your response plans in a safe environment.',
    url: 'https://thecisonetwork.com/packages/board-tabletop-exercises',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packageSchema) }}
      />
      <PageHero
        title="Board Tabletop Exercises"
        subtitle="Interactive incident response exercises for senior leadership. Practice decision-making under pressure and validate your response plans in a safe environment."
        eyebrow="Our Packages"
        seoTitle="Board Tabletop Exercises | The CISO Network"
        seoDescription="Facilitated tabletop exercises for boards and executives. Practice incident response decision-making in realistic scenarios aligned with NCSC guidance."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Practice Before the Crisis
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  When a serious cyber incident hits, the board and executive team face intense
                  pressure to make the right decisions quickly. Who approves the ransom payment?
                  When do you notify regulators? What do you tell customers?
                </p>
                <p>
                  Our Board Tabletop Exercises let you practice these decisions in a safe,
                  structured environment. Using NCSC-aligned scenarios tailored to your industry, we
                  guide your leadership team through realistic incidents to test plans, clarify
                  roles, and build confidence.
                </p>
                <p>
                  The exercises are designed for senior leadership - not technical teams. We focus
                  on the strategic decisions boards must make: communication, regulatory
                  obligations, business continuity, and crisis management.
                </p>
              </div>
            </div>

            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Users className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">At a Glance</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">Half Day</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Audience</span>
                  <span className="font-medium text-foreground">Board & Executives</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Methodology</span>
                  <span className="font-medium text-foreground">NCSC Aligned</span>
                </div>
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full justify-center"
              >
                Discuss This Package <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Exercise Scenarios
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose from proven scenarios, customised to your specific threat profile.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {scenarios.map((scenario, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-border hover:border-teal/30 transition-colors"
              >
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <Target className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  {scenario.title}
                </h3>
                <p className="text-muted-foreground text-sm">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Ideal For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {idealFor.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-teal" />
                </div>
                <p className="text-muted-foreground text-sm pt-2">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A structured approach that maximises learning while respecting your board's time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-5xl font-heading font-bold text-teal/20 mb-4">{step.step}</div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm mb-2">{step.description}</p>
                <span className="text-xs text-teal font-medium">{step.duration}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What You Receive
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Clear insights into your incident response readiness and a roadmap for improvement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliverables.map((item, index) => (
              <div
                key={index}
                className="bg-background rounded-2xl p-8 border border-border hover:border-teal/30 transition-colors"
              >
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <Shield className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Test Your Response?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Do not wait for a real incident to discover gaps in your response plans. Book a call to
            discuss scheduling a tabletop exercise for your board.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
